import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Flower2, HandHeart, Sparkles, Leaf } from "lucide-react";
import Hero from "@/components/Hero";
import FAQ from "@/components/FAQ";
import BookingCTA from "@/components/BookingCTA";
import SectionLabel from "@/components/SectionLabel";
import { EditorialSplit, Manifesto } from "@/components/Editorial";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import { HERO_IMG, PHOTO } from "@/data/site";

const services = [
    {
        n: "01",
        title: "Trattamenti viso",
        text: "Detersione, luminosità e cura mirata per una pelle che ritrova equilibrio giorno dopo giorno.",
        img: PHOTO.puliziaViso,
        to: "/estetica",
        color: "bg-rose-powder",
    },
    {
        n: "02",
        title: "Benessere del corpo",
        text: "Ogni percorso è pensato per te: leggerezza, relax e una piacevole sensazione di equilibrio.",
        img: PHOTO.donnaRilassata,
        to: "/estetica",
        color: "bg-blush/70",
    },
    {
        n: "03",
        title: "Mani e unghie",
        text: "Dalla manicure al Gelinium, fino alla Nail Art. Precisione, creatività e prodotti professionali.",
        img: PHOTO.curaMani,
        to: "/nail-art",
        color: "bg-champagne/70",
    },
    {
        n: "04",
        title: "Tecnologie estetiche",
        text: "Pressoterapia, laser, radiofrequenza e infrarossi: soluzioni innovative dentro un rituale su misura.",
        img: PHOTO.radioFrequenza,
        to: "/pressoterapia",
        color: "bg-sage/40",
    },
];

const faq = [
    {
        q: "Dove si trova il Centro Estetico Spazio Isa?",
        a: "Il centro si trova in Via A. Minghi 30, nel quartiere Coteto, a Livorno.",
    },
    {
        q: "Quali trattamenti posso trovare da Spazio Isa?",
        a: "Il centro propone trattamenti viso, trattamenti corpo, manicure, pedicure, Nail Art, Gelinium, ricostruzione unghie, pressoterapia, depilazione laser, radiofrequenza, infrarossi e molto altro.",
    },
    {
        q: "Ogni trattamento è personalizzato?",
        a: "Sì. Ogni cliente riceve una consulenza dedicata per individuare il percorso più adatto alle proprie esigenze.",
    },
    {
        q: "È possibile acquistare prodotti professionali?",
        a: "Sì, all'interno del centro sono disponibili prodotti professionali per la cura del viso, del corpo e dell'abbronzatura.",
    },
];

export default function Home() {
    return (
        <>
            <Hero
                variant="home"
                eyebrow="Oltre 15 anni al servizio della tua bellezza"
                title={["Centro Estetico", "a Livorno."]}
                subtitle="Ogni trattamento nasce dall'ascolto, dall'esperienza e dalla cura di ogni dettaglio, per farti sentire bene con te stessa ogni giorno."
                subtitleMobile="Ogni trattamento nasce dall'ascolto e dalla cura di ogni dettaglio."
                imgDesktop={HERO_IMG.home.d}
                imgMobile={HERO_IMG.home.m}
                primaryCta={{ label: "Prenota un momento", href: "/contatti" }}
                secondaryCta={{ label: "I nostri servizi", href: "/estetica" }}
            />

            {/* Marquees removed per client request */}

            {/* Manifesto 01 */}
            <Manifesto
                number="01"
                eyebrow="Benvenuta"
                title="Benvenuta da Spazio Isa, il luogo che hai sempre desiderato dedicare a te stessa."
                body={[
                    "Ogni donna dedica tempo agli altri, agli impegni quotidiani e alle mille cose da fare. Trovare uno spazio da riservare a sé stesse è un gesto prezioso, che merita attenzione e qualità.",
                    "Spazio Isa nasce con questo obiettivo: offrire un luogo dove rallentare, rilassarsi e prendersi cura della propria bellezza in un ambiente accogliente, professionale e familiare.",
                    "Da oltre quindici anni accompagniamo le nostre clienti con trattamenti personalizzati, consigli sinceri e un approccio che mette sempre la persona al centro. Per noi ogni pelle racconta una storia.",
                ]}
                accent="bg-ivory"
            />

            {/* Editorial split — molto più di un trattamento */}
            <EditorialSplit
                number="02"
                eyebrow="Il nostro approccio"
                title="Molto più di un trattamento estetico."
                image={PHOTO.dialogo}
                imageAlt="Consulenza personalizzata"
                accentBg="bg-blush/40"
                body={[
                    "Entrare da Spazio Isa significa vivere un'esperienza fatta di ascolto, professionalità e fiducia.",
                    "Ogni appuntamento inizia con un confronto, perché crediamo che comprendere le esigenze della cliente sia il primo passo per ottenere risultati soddisfacenti.",
                    "Il nostro lavoro non consiste semplicemente nell'eseguire un trattamento, ma nel creare un percorso che valorizzi la bellezza naturale di ogni persona.",
                ]}
            />

            {/* Services grid */}
            <section className="relative py-24 md:py-36 bg-ivory-warm">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-10 mb-16">
                        <div className="lg:col-span-4">
                            <Reveal>
                                <SectionLabel number="03" text="I nostri servizi" />
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <Reveal delay={0.1}>
                                <h2 className="font-display text-[32px] md:text-[46px] leading-[1.08] text-ink">
                                    Trattamenti dedicati al{" "}
                                    <em className="text-raspberry not-italic">
                                        viso
                                    </em>
                                    , al{" "}
                                    <em className="text-sage-deep not-italic">
                                        corpo
                                    </em>{" "}
                                    e alle{" "}
                                    <em className="text-raspberry not-italic">
                                        unghie
                                    </em>
                                    .
                                </h2>
                            </Reveal>
                        </div>
                    </div>

                    <Stagger className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {services.map((s, i) => (
                            <motion.div key={s.title} variants={staggerItem}>
                                <Link
                                    to={s.to}
                                    className={`group relative block overflow-hidden rounded-[28px] ${s.color} transition-all duration-500 hover:shadow-soft-lg flex flex-col`}
                                >
                                    <div className="p-6 md:p-8 flex-1">
                                        <div className="flex justify-between items-start">
                                            <span className="text-ink-muted text-[15px] md:text-[17px] tabular-nums font-semibold">
                                                {s.n}
                                            </span>
                                            <span className="w-11 h-11 rounded-full bg-white/80 text-ink grid place-items-center group-hover:bg-raspberry group-hover:text-ivory group-hover:rotate-45 transition-all duration-500">
                                                <ArrowUpRight size={18} />
                                            </span>
                                        </div>
                                        <div className="mt-10 md:mt-14">
                                            <h3 className="font-display text-[26px] md:text-[32px] leading-[1.1] text-ink">
                                                {s.title}
                                            </h3>
                                            <p className="mt-3 text-ink-soft text-[15px] md:text-[16px] leading-relaxed">
                                                {s.text}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-4 aspect-[16/10] w-full overflow-hidden">
                                        <img
                                            src={s.img}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                                        />
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* Marquee removed */}

            {/* Manifesto: Perché scegliere */}
            <section className="relative overflow-hidden py-28 md:py-40 bg-ivory">
                <div className="relative mx-auto max-w-[1200px] px-6 md:px-12 text-center">
                    <Reveal>
                        <SectionLabel number="04" text="Perché Spazio Isa" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-[32px] md:text-[46px] leading-[1.08] text-ink max-w-4xl mx-auto">
                            Perché la fiducia si costruisce, appuntamento dopo appuntamento.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-10 text-ink-soft text-lg leading-relaxed max-w-2xl mx-auto">
                            Da oltre quindici anni mettiamo passione nel nostro
                            lavoro, investendo nella formazione continua, nella
                            qualità dei prodotti e nelle tecnologie estetiche
                            più adatte alle esigenze di chi si affida a noi.
                        </p>
                    </Reveal>

                    <div className="mt-20 grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto text-left">
                        {[
                            {
                                icon: <Flower2 size={22} />,
                                n: "15+",
                                label: "Anni di esperienza",
                            },
                            {
                                icon: <HandHeart size={22} />,
                                n: "1:1",
                                label: "Consulenza dedicata",
                            },
                            {
                                icon: <Leaf size={22} />,
                                n: "100%",
                                label: "Trattamenti personalizzati",
                            },
                        ].map((s, i) => (
                            <Reveal key={i} delay={0.1 + i * 0.08}>
                                <div className="border-t border-line pt-6">
                                    <span className="w-10 h-10 rounded-full bg-rose-powder text-raspberry grid place-items-center mb-6">
                                        {s.icon}
                                    </span>
                                    <p className="font-display text-4xl md:text-5xl text-ink leading-none">
                                        {s.n}
                                    </p>
                                    <p className="mt-3 text-ink-muted text-[13px] md:text-sm uppercase tracking-[0.2em]">
                                        {s.label}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Editorial split — prodotti */}
            <EditorialSplit
                reverse
                number="05"
                eyebrow="Prodotti professionali"
                title="La bellezza continua anche dopo il trattamento."
                image={PHOTO.prodottiVari}
                imageAlt="Prodotti professionali"
                accentBg="bg-rose-powder/40"
                body={[
                    "All'interno del centro trovi una selezione di prodotti professionali per la cura del viso, del corpo e dell'abbronzatura, scelti per aiutarti a mantenere i risultati ottenuti e prenderti cura della tua pelle anche a casa.",
                    "Ogni prodotto viene proposto come parte di un percorso di cura personale, con indicazioni semplici e comprensibili per inserirlo correttamente nelle tue abitudini.",
                ]}
            />

            {/* Vieni a trovarci */}
            <section className="relative overflow-hidden py-24 md:py-32 bg-ivory">
                <div className="relative mx-auto max-w-[1200px] px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <Reveal>
                            <SectionLabel number="06" text="Vieni a trovarci" />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-display text-[32px] md:text-[46px] leading-[1.08] text-ink">
                                Ci trovi in Via A. Minghi 30, quartiere Coteto — Livorno.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-8 text-ink-soft text-[16px] leading-relaxed max-w-lg">
                                Saremo felici di accoglierti, ascoltare le tue
                                esigenze e consigliarti il trattamento più
                                adatto per prenderti cura della tua bellezza in
                                modo naturale e personalizzato.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <Link
                                to="/contatti"
                                className="mt-8 inline-flex items-center gap-3 rounded-full bg-ink text-ivory pl-6 pr-2 py-2 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-raspberry transition-all"
                            >
                                Come raggiungerci
                                <span className="w-9 h-9 rounded-full bg-ivory text-ink grid place-items-center">
                                    <ArrowUpRight size={16} />
                                </span>
                            </Link>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6">
                        <Reveal delay={0.1}>
                            <div className="relative aspect-[4/3] rounded-[28px] overflow-hidden shadow-editorial">
                                <img
                                    src={PHOTO.donnaRilassata2}
                                    alt="Spazio Isa"
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute inset-x-6 bottom-6 bg-ivory/95 backdrop-blur rounded-2xl p-6">
                                    <SectionLabel text="Orari" tone="muted" />
                                    <p className="mt-2 font-display text-xl text-ink">
                                        Mar–Ven · 09:00 – 18:30
                                    </p>
                                    <p className="text-ink-soft text-sm">
                                        Sabato · 09:00 – 13:00
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Booking CTA */}
            <BookingCTA
                eyebrow="Prenota il tuo momento"
                title="Ogni percorso inizia con un primo incontro."
                subtitle="Un momento dedicato ad ascoltare le tue esigenze e consigliarti il trattamento più adatto. Contattaci per ricevere maggiori informazioni o prenotare il tuo appuntamento."
            />

            {/* FAQ */}
            <FAQ items={faq} />
        </>
    );
}
